// ADMIN-only edit of a Section 2 Additional Certification
// catalog template. Mutates UnderwritingCertificationTemplate (the template),
// NOT the per-deal instance: the edit propagates to every deal because
// instances reference the template by code and store no text snapshot. Strict
// v1 scope — only the four author-editable copy fields. CertificationCode / Tab
// / Section are immutable here. Mirrors <CriteriaCatalogEditModal>.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateCertificationTemplate } from "@/hooks/dealFlow/useDealFlow";
import type {
  DealFlowTab,
  UnderwritingCertificationTemplateDto,
} from "@/api/dealFlow/types";

interface CertificationCatalogEditModalProps {
  open: boolean;
  onClose: () => void;
  template: UnderwritingCertificationTemplateDto;
}

const TITLE_MAX = 255;
const SHORT_DESCRIPTION_MAX = 500;

const TAB_LABEL: Record<DealFlowTab, string> = {
  Underwriting: "Underwriting",
  FinancialAnalysis: "A2 · Financial",
  LegalAnalysis: "A3 · Legal",
  BusinessAnalysis: "A4 · Business",
  Closing: "Closing",
};

export function CertificationCatalogEditModal({
  open,
  onClose,
  template,
}: CertificationCatalogEditModalProps) {
  const toast = useToast();
  const update = useUpdateCertificationTemplate();

  const [title, setTitle] = useState(template.title);
  const [shortDescription, setShortDescription] = useState(
    template.shortDescription,
  );
  const [description, setDescription] = useState(template.description);
  const [requiredEvidence, setRequiredEvidence] = useState(
    template.requiredEvidence,
  );

  // Re-seed local state when the modal is (re)opened, so a prior unsaved edit
  // on a different row never bleeds across.
  useEffect(() => {
    if (!open) return;
    setTitle(template.title);
    setShortDescription(template.shortDescription);
    setDescription(template.description);
    setRequiredEvidence(template.requiredEvidence);
  }, [open, template]);

  const titleValid = title.trim().length > 0 && title.trim().length <= TITLE_MAX;
  const shortValid =
    shortDescription.trim().length > 0 &&
    shortDescription.trim().length <= SHORT_DESCRIPTION_MAX;
  const descriptionValid = description.trim().length > 0;
  const requiredEvidenceValid = requiredEvidence.trim().length > 0;
  const formValid =
    titleValid && shortValid && descriptionValid && requiredEvidenceValid;

  const onSave = async () => {
    try {
      await update.mutateAsync({
        certificationCode: template.certificationCode,
        body: {
          title: title.trim(),
          shortDescription: shortDescription.trim(),
          description: description.trim(),
          requiredEvidence: requiredEvidence.trim(),
        },
      });
      toast.success(
        `${template.certificationCode} updated. Changes apply to all deals.`,
      );
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Edit ${template.certificationCode}`}
      description="Edits the certification template and apply to every deal — including in-progress ones."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={!formValid || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-wrap items-center gap-space-2">
          <Badge status="draft">{template.certificationCode}</Badge>
          <span className="text-caption text-gray-mid">
            {TAB_LABEL[template.tab]} · Section {template.section}
          </span>
        </div>

        <FormField label="Title" required>
          <Input
            value={title}
            maxLength={TITLE_MAX}
            invalid={!titleValid}
            onChange={(e) => setTitle(e.target.value)}
          />
        </FormField>

        <FormField
          label="Short description"
          required
          helperText="Shows under the title on the certification list."
        >
          <Textarea
            rows={2}
            value={shortDescription}
            maxLength={SHORT_DESCRIPTION_MAX}
            invalid={!shortValid}
            onChange={(e) => setShortDescription(e.target.value)}
          />
        </FormField>

        <FormField label="What you're certifying" required>
          <Textarea
            rows={6}
            value={description}
            invalid={!descriptionValid}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>

        <FormField label="What you'll need to produce" required>
          <Textarea
            rows={5}
            value={requiredEvidence}
            invalid={!requiredEvidenceValid}
            onChange={(e) => setRequiredEvidence(e.target.value)}
          />
        </FormField>
      </div>
    </Modal>
  );
}
